import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import SiteFooter from "../components/SiteFooter";

/** Slim top bar with only Back + Home */
function TopBackHome() {
  const nav = useNavigate();
  return (
    <div
      style={{
        height: 56,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        borderBottom: "1px solid #e2e8f0",
        background: "#ffffff",
        position: "sticky",
        top: 0,
        zIndex: 20,
      }}
    >
      <button
        onClick={() => nav(-1)}
        style={{
          border: 0,
          background: "transparent",
          color: "#0f172a",
          cursor: "pointer",
          fontSize: 14,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span style={{ fontSize: 18 }}>←</span>
        Back
      </button>

      <Link
        to="/"
        style={{ textDecoration: "none", color: "#0f766e", fontWeight: 700 }}
        aria-label="Go to Home"
      >
        Home
      </Link>
    </div>
  );
}

export default function Registration() {
  const navigate = useNavigate();
  const location = useLocation();

  const flash = location.state?.flash || null;

  const [form, setForm] = useState({
    referrer_id: "",
    full_name: "",
    gender: "",
    mobile: "",
    address: "",
    // ✅ NEW
    state_id: "",
    district_id: "",
    taluk_id: "",
    place: "",
    email: "",
    course_id: "",
    amount_rupees: "",
    college_id: "",
  });

  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const [referrers, setReferrers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [colleges, setColleges] = useState([]);

  // ✅ NEW dropdown data
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);

  const [loadingMasters, setLoadingMasters] = useState(true);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTaluks, setLoadingTaluks] = useState(false);

  useEffect(() => {
    if (flash) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [flash, location.pathname, navigate]);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const pickItems = (d) => (Array.isArray(d) ? d : d?.items || []);

  // Load dropdown data (referrers/courses/colleges/states)
  useEffect(() => {
    const loadMasters = async () => {
      try {
        setLoadingMasters(true);
        const { data } = await api.get("/api/registration/masters");

        setReferrers(data.referrers || []);
        setCourses(data.courses || []);
        setColleges(data.colleges || []);
        setStates(data.states || []);

        // Auto-pick first state if empty
        const firstStateId =
          form.state_id || (data.states?.length ? String(data.states[0].id) : "");

        if (firstStateId) {
          setForm((p) => ({ ...p, state_id: firstStateId }));
          await loadDistricts(firstStateId, true);
        }
      } catch (e) {
        console.error(e);
        setErr(
          e?.response?.data?.message ||
            "Failed to load dropdown values. Try again."
        );
      } finally {
        setLoadingMasters(false);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    loadMasters();
  }, []);

  const handleCourseChange = (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const selected = courses.find((c) => String(c.id) === value);
      const autoAmount =
        selected && selected.base_fee != null
          ? String(selected.base_fee)
          : prev.amount_rupees;
      return { ...prev, course_id: value, amount_rupees: autoAmount };
    });
  };

  // ✅ Load districts by state (public endpoint)
  const loadDistricts = async (stateId, autoPickFirst = false) => {
    setLoadingDistricts(true);
    setDistricts([]);
    setTaluks([]);
    try {
      const { data } = await api.get("/api/registration/districts", {
        params: { state_id: stateId },
      });
      const ds = pickItems(data);
      setDistricts(ds);

      const nextDistrictId =
        autoPickFirst && ds.length ? String(ds[0].id) : "";

      setForm((p) => ({
        ...p,
        district_id: autoPickFirst ? nextDistrictId : p.district_id,
        taluk_id: "",
      }));

      if (nextDistrictId) {
        await loadTaluks(nextDistrictId, true);
      }
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load districts");
    } finally {
      setLoadingDistricts(false);
    }
  };

  // ✅ Load taluks by district (public endpoint)
  const loadTaluks = async (districtId, autoPickFirst = false) => {
    setLoadingTaluks(true);
    setTaluks([]);
    try {
      const { data } = await api.get("/api/registration/taluks", {
        params: { district_id: districtId },
      });
      const ts = pickItems(data);
      setTaluks(ts);

      const nextTalukId =
        autoPickFirst && ts.length ? String(ts[0].id) : "";

      setForm((p) => ({
        ...p,
        taluk_id: autoPickFirst ? nextTalukId : p.taluk_id,
      }));
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load taluks");
    } finally {
      setLoadingTaluks(false);
    }
  };

  const handleStateChange = async (e) => {
    const value = e.target.value;
    setForm((p) => ({
      ...p,
      state_id: value,
      district_id: "",
      taluk_id: "",
    }));
    if (value) await loadDistricts(value, true);
  };

  const handleDistrictChange = async (e) => {
    const value = e.target.value;
    setForm((p) => ({ ...p, district_id: value, taluk_id: "" }));
    if (value) await loadTaluks(value, true);
  };

  const validate = () => {
    if (!form.referrer_id) return "Please select who referred you.";
    if (!form.full_name.trim()) return "Full name is required.";
    if (!/^[A-Za-z\s]+$/.test(form.full_name.trim()))
      return "Full name should contain only letters and spaces.";
    if (!form.gender) return "Please select gender.";
    if (!/^\d{10}$/.test(form.mobile.trim())) return "Enter 10-digit mobile.";
    if (!form.address.trim()) return "Address is required.";

    // ✅ NEW
    if (!form.state_id) return "Please select state.";
    if (!form.district_id) return "Please select district.";
    if (!form.taluk_id) return "Please select taluk.";
    if (!form.place.trim()) return "Place is required.";

    if (!form.course_id) return "Please select Delegation Type.";

    const r = Number(form.amount_rupees);
    if (!Number.isFinite(r) || r <= 0)
      return "Enter a valid amount in ₹ (greater than 0).";
    return "";
  };

  const openRazorpay = (resp) => {
    if (!window.Razorpay) {
      setErr("Razorpay script not loaded. Refresh the page.");
      return;
    }

    const {
      registrationId,
      razorpayOrderId,
      amount_minor,
      currency,
      razorpayKeyId,
      prefill,
    } = resp;

    const rzp = new window.Razorpay({
      key: razorpayKeyId,
      amount: amount_minor,
      currency,
      name: "YogaPay Registration",
      description: "Yoga program registration fee",
      order_id: razorpayOrderId,
      prefill,
      notes: { registration_id: String(registrationId) },

      handler: async function (response) {
        try {
          await api.post("/api/payments/verify", response);
          navigate(`/receipt/${registrationId}`);
        } catch (err) {
          console.error("Verify error:", err);
          const msg =
            err?.response?.data?.message ||
            err.message ||
            "Payment succeeded, but verification failed. Please contact support if it stays pending.";
          navigate(`/receipt/${registrationId}`, { state: { verifyError: msg } });
        }
      },

      modal: { ondismiss: function () {} },
      theme: { color: "#0f766e" },
    });

    rzp.open();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    const v = validate();
    if (v) return setErr(v);

    try {
      setLoading(true);

      const payload = {
        referrer_id: form.referrer_id ? Number(form.referrer_id) : null,
        full_name: form.full_name.trim(),
        gender: form.gender,
        mobile: form.mobile.trim(),
        address: form.address.trim(),

        // ✅ NEW
        state_id: form.state_id ? Number(form.state_id) : null,
        district_id: form.district_id ? Number(form.district_id) : null,
        taluk_id: form.taluk_id ? Number(form.taluk_id) : null,
        place: form.place.trim(),

        email: form.email.trim() || null,
        course_id: form.course_id ? Number(form.course_id) : null,
        amount_rupees: Number(form.amount_rupees),
        college_id: form.college_id ? Number(form.college_id) : null,
      };

      const { data } = await api.post("/api/registrations", payload);
      openRazorpay(data);
    } catch (ex) {
      const msg =
        ex?.response?.data?.message ||
        ex.message ||
        "Failed to create registration/order";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #cbd5e1",
    outline: "none",
    fontSize: 14,
    backgroundColor: "#f8fafc",
  };

  const btnStyle = {
    width: "100%",
    padding: "12px",
    borderRadius: 10,
    border: 0,
    background: "#0f766e",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  };

  const disabledAny =
    loading || loadingMasters || loadingDistricts || loadingTaluks;

  return (
    <>
      <TopBackHome />

      <div
        style={{
          minHeight: "calc(100vh - 56px)",
          display: "grid",
          placeItems: "center",
          background: "#f6f8fa",
          padding: 24,
        }}
      >
        <form
          onSubmit={onSubmit}
          style={{
            width: "100%",
            maxWidth: 520,
            background: "#fff",
            padding: 24,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
          }}
        >
          <h2 style={{ margin: 0, marginBottom: 10 }}>Registration</h2>
          <p style={{ marginTop: 0, color: "#475569" }}>
            Pay online via UPI/Razorpay
          </p>

          {flash && (
            <div
              style={{
                background: flash.type === "success" ? "#d1fae5" : "#fee2e2",
                border: `1px solid ${
                  flash.type === "success" ? "#10b981" : "#ef4444"
                }`,
                color: flash.type === "success" ? "#065f46" : "#7f1d1d",
                padding: 12,
                borderRadius: 8,
                marginBottom: 12,
              }}
            >
              {flash.text}
            </div>
          )}

          {loadingMasters && (
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 8 }}>
              Loading options…
            </div>
          )}

          <label style={{ display: "block", marginTop: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
              Reffed by
            </span>
            <select
              name="referrer_id"
              value={form.referrer_id}
              onChange={onChange}
              style={inputStyle}
            >
              <option value="">Select</option>
              {referrers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: "block", marginTop: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
              Full Name
            </span>
            <input
              name="full_name"
              value={form.full_name}
              onChange={onChange}
              placeholder="Your name"
              style={inputStyle}
            />
          </label>

          <label style={{ display: "block", marginTop: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
              College (Optional)
            </span>
            <select
              name="college_id"
              value={form.college_id}
              onChange={onChange}
              style={inputStyle}
            >
              <option value="">Select college (optional)</option>
              {colleges.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <div style={{ marginTop: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
              Gender
            </span>
            <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
              <label style={{ fontSize: 14 }}>
                <input
                  type="radio"
                  name="gender"
                  value="M"
                  checked={form.gender === "M"}
                  onChange={onChange}
                />{" "}
                Male
              </label>
              <label style={{ fontSize: 14 }}>
                <input
                  type="radio"
                  name="gender"
                  value="F"
                  checked={form.gender === "F"}
                  onChange={onChange}
                />{" "}
                Female
              </label>
            </div>
          </div>

          <label style={{ display: "block", marginTop: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
              Mobile (10 digits)
            </span>
            <input
              name="mobile"
              value={form.mobile}
              onChange={onChange}
              placeholder="9999999999"
              style={inputStyle}
              maxLength={10}
            />
          </label>

          <label style={{ display: "block", marginTop: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
              Address
            </span>
            <textarea
              name="address"
              value={form.address}
              onChange={onChange}
              placeholder="Address"
              rows={3}
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </label>

          {/* ✅ NEW: State/District/Taluk/Place */}
          <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
            <label style={{ display: "block" }}>
              <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
                State
              </span>
              <select
                name="state_id"
                value={form.state_id}
                onChange={handleStateChange}
                style={inputStyle}
              >
                <option value="">Select state</option>
                {states.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "block" }}>
              <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
                District
              </span>
              <select
                name="district_id"
                value={form.district_id}
                onChange={handleDistrictChange}
                style={inputStyle}
                disabled={!form.state_id || loadingDistricts}
              >
                <option value="">{loadingDistricts ? "Loading..." : "Select district"}</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "block" }}>
              <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
                Taluk
              </span>
              <select
                name="taluk_id"
                value={form.taluk_id}
                onChange={onChange}
                style={inputStyle}
                disabled={!form.district_id || loadingTaluks}
              >
                <option value="">{loadingTaluks ? "Loading..." : "Select taluk"}</option>
                {taluks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "block" }}>
              <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
                Place
              </span>
              <input
                name="place"
                value={form.place}
                onChange={onChange}
                placeholder="Place / locality"
                style={inputStyle}
              />
            </label>
          </div>

          <label style={{ display: "block", marginTop: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
              Email (optional)
            </span>
            <input
              name="email"
              value={form.email}
              onChange={onChange}
              placeholder="you@example.com"
              style={inputStyle}
            />
          </label>

          <label style={{ display: "block", marginTop: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
              Delegation Type
            </span>
            <select
              name="course_id"
              value={form.course_id}
              onChange={handleCourseChange}
              style={inputStyle}
            >
              <option value="">Select Delegation Type</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.base_fee != null ? ` (₹${c.base_fee})` : ""}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: "block", marginTop: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
              Amount (₹)
            </span>
            <input
              name="amount_rupees"
              value={form.amount_rupees}
              onChange={onChange}
              placeholder="Amount to pay"
              style={inputStyle}
              type="number"
              min="0"
              step="0.01"
            />
          </label>

          {err ? (
            <div style={{ color: "#b91c1c", marginTop: 10 }}>{err}</div>
          ) : null}

          <button type="submit" disabled={disabledAny} style={btnStyle}>
            {loading ? "Creating order..." : "Pay & Register"}
          </button>

          <p style={{ fontSize: 12, color: "#64748b", marginTop: 12 }}>
            After payment, we’ll show your final status automatically.
          </p>
        </form>
      </div>

      <SiteFooter />
    </>
  );
}
